/**
 * CorporateIdentitySection — Premium CI Style Guide für alle Marken der Firmengruppe
 * 
 * Sticky Tabs mit echten Logos in der Kopfzeile.
 * Echte Farben direkt von den Webseiten abgeglichen.
 * Hochwertiges, mikro-minimalistisches Design.
 */
import { useState, useEffect, useRef } from 'react';
import {
  Palette, Type, Image, Mail, Copy, Check,
  Smartphone, Phone, Globe, AtSign, ChevronDown, ChevronRight,
  Building2, Loader2, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { useMarkenConfig, type MarkenConfig } from '@/hooks/useAdminSettings';

// ─── Marken-spezifische CI-Daten (ECHTE Farben von den Webseiten) ────────
interface MarkenCI {
  farben: {
    primaer: { name: string; hex: string; usage: string }[];
    sekundaer: { name: string; hex: string; usage: string }[];
    neutral: { name: string; hex: string; usage: string }[];
  };
  logos: { name: string; file: string; usage: string; bg: string }[];
  firma: { label: string; value: string }[];
  signatur?: {
    name: string;
    titel: string;
    firma: string;
    adresse: string;
    mobil: string;
    festnetz: string;
    email: string;
    website: string;
  };
  tabLogo?: string;
  tabIcon?: string; // Fallback-Buchstabe für Tab
}

const MARKEN_CI: Record<string, MarkenCI> = {
  gutachter_koop: {
    tabLogo: '/logo-teal.png',
    farben: {
      primaer: [
        { name: 'Koop Teal', hex: '#0e7490', usage: 'Primärfarbe, Logo, Überschriften' },
        { name: 'Koop Cyan', hex: '#0891b2', usage: 'Links, Buttons, Akzente' },
        { name: 'Koop Cyan Hell', hex: '#06b6d4', usage: 'Hover-Zustände, Highlights' },
      ],
      sekundaer: [
        { name: 'Koop Navy', hex: '#1a1a2e', usage: 'Hintergründe, Hero-Bereich' },
        { name: 'Koop Dunkel', hex: '#16213e', usage: 'Footer, dunkle Panels' },
      ],
      neutral: [
        { name: 'Text Dunkel', hex: '#374151', usage: 'Fließtext, Kontaktdaten' },
        { name: 'Text Mittel', hex: '#64748b', usage: 'Sekundärer Text, Labels' },
        { name: 'Text Hell', hex: '#9CA3AF', usage: 'Platzhalter, Fußzeilen' },
        { name: 'Border', hex: '#e2e8f0', usage: 'Trennlinien, Rahmen' },
      ],
    },
    logos: [
      { name: 'Logo Teal', file: '/logo-teal.png', usage: 'Standard — heller Hintergrund', bg: 'bg-white' },
      { name: 'Logo Dunkel', file: '/logo-dark.png', usage: 'E-Mail-Signatur, Dokumente', bg: 'bg-white' },
      { name: 'Logo Weiß', file: '/logo-white-full.png', usage: 'Dunkle Hintergründe, Footer', bg: 'bg-[#1a1a2e]' },
      { name: 'Logo Signatur', file: '/logo-teal-sig.png', usage: 'E-Mail-Signatur (kompakt)', bg: 'bg-white' },
    ],
    firma: [
      { label: 'Firma', value: 'SV Büro Koop' },
      { label: 'Inhaber', value: 'Andreas Koop' },
      { label: 'Titel', value: 'Kfz-Techniker-Meister & Kfz-Sachverständiger' },
      { label: 'Adresse', value: 'Rahmedestraße 65, 58507 Lüdenscheid' },
      { label: 'Mobil', value: '0170 500 4022' },
      { label: 'Festnetz', value: '02351 926 5740' },
      { label: 'E-Mail', value: 'info@gutachter-koop.de' },
      { label: 'Webseite', value: 'www.gutachter-koop.de' },
    ],
    signatur: {
      name: 'Andreas Koop',
      titel: 'Kfz-Techniker-Meister & Kfz-Sachverständiger',
      firma: 'SV Büro Koop',
      adresse: 'Rahmedestraße 65 · 58507 Lüdenscheid',
      mobil: '0170 500 4022',
      festnetz: '02351 926 5740',
      email: 'info@gutachter-koop.de',
      website: 'gutachter-koop.de',
    },
  },
  unfallgiganten: {
    tabLogo: '/logos/unfallgiganten-logo.svg',
    farben: {
      primaer: [
        { name: 'UG Rot', hex: '#B0172F', usage: 'Primärfarbe, Logo, Top-Bar' },
        { name: 'UG Rot Hell', hex: '#DC2626', usage: 'Buttons, CTAs, Akzente' },
        { name: 'UG Rot Dunkel', hex: '#8B1225', usage: 'Hover-Zustände, aktive Elemente' },
      ],
      sekundaer: [
        { name: 'UG Schwarz', hex: '#111827', usage: 'Hintergründe, Hero-Bereich' },
        { name: 'UG Dunkelgrau', hex: '#1F2937', usage: 'Karten, Panels' },
      ],
      neutral: [
        { name: 'Text Hell', hex: '#F9FAFB', usage: 'Text auf dunklem Hintergrund' },
        { name: 'Text Grau', hex: '#9CA3AF', usage: 'Sekundärer Text' },
        { name: 'Border', hex: '#374151', usage: 'Trennlinien auf dunklem BG' },
      ],
    },
    logos: [
      { name: 'Logo Standard', file: '/logos/unfallgiganten-logo.svg', usage: 'Heller Hintergrund', bg: 'bg-white' },
      { name: 'Logo WebP', file: '/logos/unfallgiganten-logo.webp', usage: 'Fallback-Format', bg: 'bg-white' },
    ],
    firma: [
      { label: 'Marke', value: 'Unfallgiganten' },
      { label: 'Domain', value: 'www.unfallgiganten.de' },
      { label: 'Typ', value: 'Plattform für Unfallabwicklung' },
      { label: 'Zielgruppe', value: 'Autofahrer nach Unfall' },
    ],
    signatur: {
      name: 'Unfallgiganten',
      titel: 'Ihre Unfallabwicklung — einfach & schnell',
      firma: 'Unfallgiganten',
      adresse: 'Rahmedestraße 65 · 58507 Lüdenscheid',
      mobil: '0170 500 4022',
      festnetz: '02351 926 5740',
      email: 'info@unfallgiganten.de',
      website: 'unfallgiganten.de',
    },
  },
  mietgo: {
    tabLogo: '/logos/mietgo-logo.png',
    farben: {
      primaer: [
        { name: 'MietGo Blau', hex: '#0056B3', usage: 'Logo "Go", Header, Buttons' },
        { name: 'MietGo Navy', hex: '#0A1628', usage: 'Logo "Miet", Hero, Headings' },
        { name: 'MietGo Blau Hell', hex: '#2980D9', usage: 'Links, Hover, Akzente' },
      ],
      sekundaer: [
        { name: 'MietGo Akzent', hex: '#E8553D', usage: 'CTAs, Highlight-Badges, Preise' },
        { name: 'MietGo i-Punkt', hex: '#0056B3', usage: 'Markenzeichen: blauer i-Punkt' },
      ],
      neutral: [
        { name: 'Text Dunkel', hex: '#1F2937', usage: 'Fließtext, Überschriften' },
        { name: 'Text Mittel', hex: '#6B7280', usage: 'Labels, Beschreibungen' },
        { name: 'Hintergrund', hex: '#F0F9FF', usage: 'Seitenhintergrund' },
        { name: 'Weiß', hex: '#FFFFFF', usage: 'Karten, Eingabefelder' },
      ],
    },
    logos: [
      { name: 'Wordmark', file: '/logos/mietgo-logo.png', usage: 'Hauptlogo, Header', bg: 'bg-white' },
      { name: 'Wordmark Dark', file: '/logos/mietgo-logo-dark.png', usage: 'Auf dunklem Hintergrund', bg: 'bg-gray-900' },
      { name: 'App Icon', file: '/logos/mietgo-icon.png', usage: 'App-Icon, Favicon', bg: 'bg-white' },
      { name: 'Signatur', file: '/logos/mietgo-logo-sig.png', usage: 'E-Mail-Signatur', bg: 'bg-white' },
      { name: 'Logo WebP', file: '/logos/mietgo-logo.webp', usage: 'Webseite (optimiert)', bg: 'bg-white' },
    ],
    firma: [
      { label: 'Marke', value: 'MietGo' },
      { label: 'Domain', value: 'www.mietgo.de' },
      { label: 'Typ', value: 'Mietwagen & Ersatzfahrzeuge' },
      { label: 'Betreiber', value: 'LCS GmbH' },
      { label: 'Slogan', value: 'Mobilität. Einfach. Jetzt.' },
      { label: 'Schriftart', value: 'Montserrat / Inter' },
    ],
  },
  protex: {
    tabIcon: 'P',
    farben: {
      primaer: [
        { name: 'Protex Rot', hex: '#B0172F', usage: 'Primärfarbe, Logo' },
        { name: 'Protex Rot Hell', hex: '#DC2626', usage: 'Buttons, Akzente' },
        { name: 'Protex Rot Dunkel', hex: '#8B1225', usage: 'Hover-Zustände' },
      ],
      sekundaer: [
        { name: 'Protex Schwarz', hex: '#111827', usage: 'Hintergründe' },
        { name: 'Protex Weiß', hex: '#FAFAFA', usage: 'Karten, Panels' },
      ],
      neutral: [
        { name: 'Text Dunkel', hex: '#1F2937', usage: 'Fließtext' },
        { name: 'Text Mittel', hex: '#6B7280', usage: 'Labels' },
        { name: 'Border', hex: '#E5E7EB', usage: 'Trennlinien' },
      ],
    },
    logos: [],
    firma: [
      { label: 'Marke', value: 'Protex' },
      { label: 'Typ', value: 'Schutz & Prävention' },
      { label: 'Betreiber', value: 'LCS GmbH' },
    ],
  },
  lenneglas: {
    tabLogo: '/logos/lenneglas-logo.webp',
    farben: {
      primaer: [
        { name: 'LG Teal', hex: '#0D9488', usage: 'Primärfarbe, Logo, Buttons' },
        { name: 'LG Teal Hell', hex: '#14B8A6', usage: 'Akzente, Hover' },
        { name: 'LG Teal Dunkel', hex: '#0F766E', usage: 'Header, dunkle Bereiche' },
      ],
      sekundaer: [
        { name: 'LG Navy', hex: '#0C1B2A', usage: 'Hero-Hintergrund' },
        { name: 'LG Emerald', hex: '#10B981', usage: 'CTA-Buttons' },
      ],
      neutral: [
        { name: 'Text Dunkel', hex: '#1F2937', usage: 'Fließtext' },
        { name: 'Text Mittel', hex: '#6B7280', usage: 'Labels' },
        { name: 'Border', hex: '#D1FAE5', usage: 'Trennlinien' },
      ],
    },
    logos: [
      { name: 'Logo Standard', file: '/logos/lenneglas-logo.webp', usage: 'Heller Hintergrund', bg: 'bg-white' },
    ],
    firma: [
      { label: 'Marke', value: 'Lenneglas' },
      { label: 'Domain', value: 'www.lenneglas.de' },
      { label: 'Typ', value: 'Autoglas-Reparatur & Austausch' },
      { label: 'Betreiber', value: 'Gutachter KOOP Firmengruppe' },
    ],
  },
  unfallwiki: {
    tabIcon: 'UW',
    farben: {
      primaer: [
        { name: 'UW Rot', hex: '#B0172F', usage: 'Primärfarbe, Logo, Navigation' },
        { name: 'UW Rot Hell', hex: '#DC2626', usage: 'Links, Buttons, Akzente' },
        { name: 'UW Rot Dunkel', hex: '#8B1225', usage: 'Hover-Zustände' },
      ],
      sekundaer: [
        { name: 'UW Schwarz', hex: '#111827', usage: 'Hintergründe, Hero' },
        { name: 'UW Dunkelgrau', hex: '#1F2937', usage: 'Karten, Panels' },
      ],
      neutral: [
        { name: 'Text Hell', hex: '#F9FAFB', usage: 'Text auf dunklem Hintergrund' },
        { name: 'Text Grau', hex: '#9CA3AF', usage: 'Sekundärer Text' },
        { name: 'Border', hex: '#374151', usage: 'Trennlinien' },
      ],
    },
    logos: [],
    firma: [
      { label: 'Marke', value: 'UnfallWiki' },
      { label: 'Domain', value: 'www.unfallwiki.de' },
      { label: 'Typ', value: 'Wissensdatenbank für Unfallrecht' },
      { label: 'Verbindung', value: 'Verlinkt auf unfallgiganten.de' },
    ],
  },
  schadenleicht: {
    tabLogo: '/logos/schadenleicht-icon.png',
    farben: {
      primaer: [
        { name: 'SL Blau', hex: '#1A73E8', usage: 'Primärfarbe, Logo, Buttons' },
        { name: 'SL Blau Dunkel', hex: '#1557B0', usage: 'Hover-Zustände, Header' },
        { name: 'SL Blau Hell', hex: '#4285F4', usage: 'Links, Akzente' },
      ],
      sekundaer: [
        { name: 'SL Schwarz', hex: '#1F2937', usage: 'Texte, Überschriften' },
        { name: 'SL Weiß', hex: '#FFFFFF', usage: 'Hintergründe, Karten' },
      ],
      neutral: [
        { name: 'Text Dunkel', hex: '#111827', usage: 'Fließtext' },
        { name: 'Text Mittel', hex: '#6B7280', usage: 'Labels' },
        { name: 'Border', hex: '#E5E7EB', usage: 'Trennlinien' },
      ],
    },
    logos: [
      { name: 'App-Icon', file: '/logos/schadenleicht-icon.png', usage: 'App-Icon, Favicon', bg: 'bg-white' },
    ],
    firma: [
      { label: 'Marke', value: 'SCHADENLEICHT' },
      { label: 'Domain', value: 'www.schadenleicht.de' },
      { label: 'Typ', value: 'Dachmarke — Schaden leicht gemacht' },
      { label: 'Betreiber', value: 'Gutachter KOOP Firmengruppe' },
    ],
  },
};

// Marken-Reihenfolge (Gutachter Koop immer zuerst)
const MARKEN_ORDER = [
  'gutachter_koop', 'unfallgiganten', 'lenneglas', 'unfallwiki', 
  'mietgo', 'protex', 'schadenleicht',
];

// ─── Hilfskomponenten ──────────────────────────────────────────

function ColorSwatch({ color }: { color: { name: string; hex: string; usage: string } }) {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(color.hex);
    setCopied(true);
    toast.success(`${color.hex} kopiert`);
    setTimeout(() => setCopied(false), 2000);
  };

  const isLight = ['#FAFAFA', '#F9FAFB', '#F0F9FF', '#FFFFFF', '#D1FAE5', '#ECFDF5', '#E5E7EB', '#e2e8f0', '#F8FAFC'].includes(color.hex);

  return (
    <div className="group cursor-pointer flex-1 min-w-[100px] max-w-[160px]" onClick={handleCopy}>
      <div 
        className="h-14 rounded-xl mb-2.5 transition-all duration-200 group-hover:scale-[1.03] group-hover:shadow-lg relative overflow-hidden"
        style={{ 
          backgroundColor: color.hex,
          border: isLight ? '1px solid #E5E7EB' : 'none',
          boxShadow: `0 2px 8px ${color.hex}20`,
        }}
      >
        {/* Subtle glass effect */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="bg-black/30 backdrop-blur-sm rounded-full p-1.5">
            {copied ? <Check className="w-3.5 h-3.5 text-white" /> : <Copy className="w-3 h-3 text-white" />}
          </div>
        </div>
      </div>
      <p className="text-[11px] font-semibold text-gray-900 leading-tight">{color.name}</p>
      <p className="text-[10px] font-mono text-gray-400 mt-0.5">{color.hex.toUpperCase()}</p>
      <p className="text-[9px] text-gray-400 leading-tight mt-0.5">{color.usage}</p>
    </div>
  );
}

function SectionCard({ icon: Icon, title, subtitle, children, defaultOpen = true }: { 
  icon: any; title: string; subtitle: string; children: React.ReactNode; defaultOpen?: boolean 
}) {
  const [open, setOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden transition-shadow hover:shadow-md">
      <div 
        className="flex items-center gap-3 p-5 cursor-pointer select-none"
        onClick={() => setOpen(!open)}
      >
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center flex-shrink-0 border border-gray-100">
          <Icon className="w-4 h-4 text-gray-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[13px] font-bold text-gray-900">{title}</h3>
          <p className="text-[10px] text-gray-400 mt-0.5">{subtitle}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-300 transition-transform duration-200 ${open ? '' : '-rotate-90'}`} />
      </div>
      {open && <div className="px-5 pb-5 -mt-1">{children}</div>}
    </div>
  );
}

function SignaturePreview({ marke, ci }: { marke: MarkenConfig; ci: MarkenCI }) {
  const sig = ci.signatur;
  if (!sig) return null;
  const primary = marke.farbe_primaer || '#0e7490';

  return (
    <SectionCard icon={Mail} title="E-Mail-Signatur" subtitle={`Signatur-Vorschau für ${marke.anzeigename}`}>
      <div className="p-5 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-100">
        <div style={{ fontFamily: "'Helvetica Neue', Arial, sans-serif", maxWidth: 440 }}>
          {/* Top accent line */}
          <div style={{ height: 3, borderRadius: 2, background: `linear-gradient(90deg, ${primary}, ${primary}80)`, marginBottom: 14 }} />
          
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1A1A2E', lineHeight: 1.3 }}>{sig.name}</div>
            <div style={{ fontSize: 10, fontWeight: 600, color: primary, letterSpacing: '0.8px', textTransform: 'uppercase' as const, lineHeight: 1.4, marginTop: 2 }}>
              {sig.titel}
            </div>
          </div>
          
          <div style={{ height: 1, background: '#E5E7EB', marginBottom: 10 }} />
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Smartphone style={{ width: 13, height: 13, color: primary }} />
              <span style={{ fontSize: 11.5, color: '#374151' }}>{sig.mobil}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Phone style={{ width: 13, height: 13, color: primary }} />
              <span style={{ fontSize: 11.5, color: '#374151' }}>{sig.festnetz}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Mail style={{ width: 13, height: 13, color: primary }} />
              <span style={{ fontSize: 11.5, color: primary }}>{sig.email}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Globe style={{ width: 13, height: 13, color: primary }} />
              <span style={{ fontSize: 11.5, color: primary }}>{sig.website}</span>
            </div>
          </div>
          
          <div style={{ height: 1, background: '#E5E7EB', marginTop: 10 }} />
          <div style={{ marginTop: 8, fontSize: 9.5, color: '#9CA3AF' }}>
            <strong style={{ color: '#6B7280' }}>{sig.firma}</strong> · {sig.adresse}
          </div>
          <div style={{ marginTop: 6, fontSize: 8, color: '#C0C5CD', lineHeight: 1.4 }}>
            Diese E-Mail ist vertraulich und ausschließlich für den Adressaten bestimmt.
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

// ─── Marken-Tab-Inhalt ──────────────────────────────────────────

function MarkenCIContent({ marke }: { marke: MarkenConfig }) {
  const ci = MARKEN_CI[marke.marke] || {
    farben: {
      primaer: [{ name: 'Primär', hex: marke.farbe_primaer || '#6B7280', usage: 'Primärfarbe' }],
      sekundaer: [{ name: 'Sekundär', hex: marke.farbe_sekundaer || '#9CA3AF', usage: 'Sekundärfarbe' }],
      neutral: [
        { name: 'Text Dunkel', hex: '#374151', usage: 'Fließtext' },
        { name: 'Text Mittel', hex: '#6B7280', usage: 'Labels' },
      ],
    },
    logos: [],
    firma: [
      { label: 'Marke', value: marke.anzeigename },
      { label: 'Domain', value: marke.domain || '—' },
    ],
  };

  return (
    <div className="space-y-3">
      {/* Marken-Header mit Gradient */}
      <div 
        className="rounded-2xl p-6 text-white relative overflow-hidden"
        style={{ 
          background: `linear-gradient(135deg, ${marke.farbe_primaer || '#374151'} 0%, ${marke.farbe_sekundaer || marke.farbe_primaer || '#6B7280'} 100%)` 
        }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.12),transparent_60%)]" />
        <div className="relative z-10 flex items-center gap-4">
          {ci.tabLogo ? (
            <div className="w-14 h-14 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center p-2 border border-white/10">
              <img src={ci.tabLogo} alt={marke.anzeigename} className="max-h-10 max-w-full object-contain" />
            </div>
          ) : (
            <div className="w-14 h-14 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/10">
              <span className="text-xl font-bold text-white/90">{ci.tabIcon || marke.anzeigename.charAt(0)}</span>
            </div>
          )}
          <div>
            <h2 className="text-lg font-bold">{marke.anzeigename}</h2>
            <p className="text-xs text-white/70 mt-0.5">{marke.beschreibung || 'Corporate Identity & Style Guide'}</p>
            {marke.domain && (
              <a 
                href={`https://www.${marke.domain}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[10px] text-white/60 hover:text-white/90 mt-1 transition-colors"
              >
                <Globe className="w-3 h-3" />
                {marke.domain}
                <ExternalLink className="w-2.5 h-2.5" />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Farbpalette */}
      <SectionCard icon={Palette} title="Farbpalette" subtitle="Klicken zum Kopieren des Hex-Codes">
        <div className="space-y-6">
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.1em] mb-3">Primärfarben</p>
            <div className="flex flex-wrap gap-3">
              {ci.farben.primaer.map(c => <ColorSwatch key={c.hex} color={c} />)}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.1em] mb-3">Sekundärfarben</p>
            <div className="flex flex-wrap gap-3">
              {ci.farben.sekundaer.map(c => <ColorSwatch key={c.hex} color={c} />)}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.1em] mb-3">Neutrale Farben</p>
            <div className="flex flex-wrap gap-3">
              {ci.farben.neutral.map(c => <ColorSwatch key={c.hex} color={c} />)}
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Logo-Varianten */}
      {ci.logos.length > 0 && (
        <SectionCard icon={Image} title="Logo-Varianten" subtitle="Alle verfügbaren Logo-Versionen">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {ci.logos.map(logo => (
              <div key={logo.file} className="border border-gray-100 rounded-xl overflow-hidden group hover:shadow-md transition-shadow">
                <div className={`${logo.bg} p-5 flex items-center justify-center h-24`}>
                  <img src={logo.file} alt={logo.name} className="max-h-14 max-w-full object-contain transition-transform group-hover:scale-105" />
                </div>
                <div className="p-3 bg-gradient-to-b from-gray-50 to-white">
                  <p className="text-[10px] font-bold text-gray-800">{logo.name}</p>
                  <p className="text-[9px] text-gray-400 mt-0.5">{logo.usage}</p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Typografie */}
      <SectionCard icon={Type} title="Typografie" subtitle="Schriftarten und Größen">
        <div className="border border-gray-100 rounded-xl p-5 bg-gradient-to-br from-gray-50/50 to-white">
          <div className="flex items-baseline justify-between mb-4">
            <span className="text-base font-bold text-gray-900">Inter / System UI</span>
            <span className="text-[9px] font-mono text-gray-300 bg-gray-50 px-2 py-0.5 rounded-full">App & Web</span>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-bold" style={{ color: marke.farbe_primaer || '#1F2937' }}>Überschrift — 14px Bold</p>
            <p className="text-xs font-semibold text-gray-700">Untertitel — 12px Semibold</p>
            <p className="text-xs text-gray-600">Fließtext — 12px Regular</p>
            <p className="text-[10px] text-gray-400">Meta-Text — 10px Regular</p>
            <p className="text-[9px] text-gray-300 font-mono">Mono — 9px (Code, Hex-Werte)</p>
          </div>
        </div>
      </SectionCard>

      {/* Signatur-Vorschau */}
      {ci.signatur && <SignaturePreview marke={marke} ci={ci} />}

      {/* Firmendaten */}
      <SectionCard icon={Building2} title="Firmendaten" subtitle="Offizielle Daten für Kommunikation">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {ci.firma.map(item => (
            <div key={item.label} className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-50">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{item.label}</span>
              <span className="text-[11px] text-gray-800 font-medium">{item.value}</span>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────

export default function CorporateIdentitySection() {
  const { data: marken, loading } = useMarkenConfig();
  const [activeMarke, setActiveMarke] = useState<string>('gutachter_koop');
  const tabsRef = useRef<HTMLDivElement>(null);

  // Sortierung nach definierter Reihenfolge
  const sortedMarken = [...marken].sort((a, b) => {
    const aIdx = MARKEN_ORDER.indexOf(a.marke);
    const bIdx = MARKEN_ORDER.indexOf(b.marke);
    if (aIdx === -1 && bIdx === -1) return a.anzeigename.localeCompare(b.anzeigename);
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });

  const selectedMarke = sortedMarken.find(m => m.marke === activeMarke) || sortedMarken[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
        <span className="ml-3 text-sm text-gray-400">Marken werden geladen...</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Sticky Header mit Marken-Tabs */}
      <div className="sticky top-0 z-20 -mx-1 px-1">
        <div className="bg-white/95 backdrop-blur-md rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-5 pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center border border-gray-100">
                <Palette className="w-4 h-4 text-gray-500" />
              </div>
              <div>
                <h2 className="text-[13px] font-bold text-gray-900">Corporate Identity</h2>
                <p className="text-[10px] text-gray-400">Style Guide aller Marken der Firmengruppe</p>
              </div>
            </div>
          </div>

          {/* Scrollbare Marken-Tabs mit Logos */}
          <div 
            ref={tabsRef}
            className="flex gap-1 px-4 pb-3 overflow-x-auto scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {sortedMarken.map(m => {
              const isActive = m.marke === activeMarke;
              const ci = MARKEN_CI[m.marke];
              const logo = ci?.tabLogo;
              const icon = ci?.tabIcon;

              return (
                <button
                  key={m.marke}
                  onClick={() => setActiveMarke(m.marke)}
                  className={`
                    flex items-center gap-2 px-3 py-2 rounded-xl text-[11px] font-semibold 
                    transition-all duration-200 whitespace-nowrap flex-shrink-0
                    ${isActive 
                      ? 'text-white shadow-lg scale-[1.02]' 
                      : 'bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700 border border-gray-100'
                    }
                  `}
                  style={isActive ? { 
                    background: `linear-gradient(135deg, ${m.farbe_primaer || '#6B7280'}, ${m.farbe_sekundaer || m.farbe_primaer || '#9CA3AF'})`,
                    boxShadow: `0 4px 14px ${m.farbe_primaer || '#6B7280'}40`,
                  } : undefined}
                >
                  {/* Logo oder Icon im Tab */}
                  {logo ? (
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 overflow-hidden ${isActive ? 'bg-white/20' : 'bg-white'}`}>
                      <img 
                        src={logo} 
                        alt="" 
                        className="w-4 h-4 object-contain"
                        style={isActive ? { filter: 'brightness(10)' } : undefined}
                      />
                    </div>
                  ) : icon ? (
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 text-[9px] font-black ${isActive ? 'bg-white/20 text-white' : 'text-gray-400'}`}
                      style={!isActive ? { backgroundColor: `${m.farbe_primaer || '#6B7280'}15` } : undefined}
                    >
                      {icon}
                    </div>
                  ) : (
                    <div 
                      className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 text-[9px] font-black ${isActive ? 'bg-white/20 text-white' : 'text-white'}`}
                      style={!isActive ? { backgroundColor: m.farbe_primaer || '#6B7280' } : undefined}
                    >
                      {m.anzeigename.charAt(0)}
                    </div>
                  )}
                  {m.anzeigename}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Marken-spezifischer Inhalt */}
      {selectedMarke && <MarkenCIContent marke={selectedMarke} />}
    </div>
  );
}
